import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Card, Collapse, Tabs, Tag, Empty, Typography } from 'antd'
import {
  BellOutlined,
  BugOutlined,
  ThunderboltOutlined,
  StarOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import {
  ANNOUNCEMENTS,
  ANNOUNCEMENT_PLATFORMS,
  hasUnreadAnnouncement,
  latestVersionOf,
  platformOf,
  readKeyOf
} from '../data/announcements'

// 店家公告頁不顯示 bug 修復項目（避免讓店家覺得網站很多 bug）；
// bug 修復改在 admin 端的更新紀錄頁完整列出。
const SHOP_VISIBLE_KINDS = new Set(['feature', 'improvement'])

// 從原始資料過濾出店家可見的版本與 sections。
// 若某個版本所有 section 都是 bugfix，整個版本就不出現在店家公告頁。
const buildShopAnnouncements = (raw, platform) =>
  raw
    .filter((ann) => platformOf(ann) === platform)
    .map((ann) => ({
      ...ann,
      sections: (ann.sections || []).filter((s) =>
        SHOP_VISIBLE_KINDS.has(s.kind)
      )
    }))
    .filter((ann) => ann.sections.length > 0)

const { Title, Paragraph, Text } = Typography
const { Panel } = Collapse

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  h1 {
    font-size: 28px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 22px;
    }
  }
`

const SubHint = styled.div`
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 24px;
`

const VersionCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
  border: none;

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }

  /* antd Card title 預設 white-space: nowrap + text-overflow: ellipsis，
     在窄螢幕（手機）會把長標題切掉。改成允許換行、長中英文也會斷字。*/
  .ant-card-head-title {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`

const VersionHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .version-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    /* 讓 flex 子元素能正確換行而非撐爆容器 */
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 1.4;
  }

  .version-date {
    color: #6b7280;
    font-size: 14px;
  }

  /* 手機螢幕窄，標題吃滿一行、日期跟 tag 退到下一行更易讀 */
  @media (max-width: 480px) {
    .version-title {
      font-size: 16px;
      flex-basis: 100%;
    }
  }
`

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-weight: 500;
  color: #1a1a1a;
`

const HowToUseBlock = styled.div`
  background: #f9fafb;
  border-left: 3px solid #7c3aed;
  border-radius: 6px;
  padding: 12px 16px;
  margin-top: 12px;

  .how-to-heading {
    font-weight: 600;
    color: #4c1d95;
    margin-bottom: 4px;
    font-size: 13px;
    letter-spacing: 0.5px;
  }
`

const StepGroup = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  .group-heading {
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }

  ol {
    margin: 0;
    padding-left: 20px;
    color: #374151;

    li {
      margin-bottom: 4px;
      line-height: 1.7;
    }
  }
`

const KIND_META = {
  feature: {
    label: '新功能',
    color: 'purple',
    icon: <StarOutlined />
  },
  bugfix: {
    label: 'Bug 修復',
    color: 'red',
    icon: <BugOutlined />
  },
  improvement: {
    label: '體驗改善',
    color: 'cyan',
    icon: <ThunderboltOutlined />
  }
}

const renderHowToUse = (section) => {
  if (section.howToUseSteps && Array.isArray(section.howToUseSteps)) {
    return (
      <HowToUseBlock>
        <div className='how-to-heading'>店家怎麼用</div>
        {section.howToUseSteps.map((group, idx) => (
          <StepGroup key={idx}>
            {group.heading && (
              <div className='group-heading'>{group.heading}</div>
            )}
            <ol>
              {group.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </StepGroup>
        ))}
      </HowToUseBlock>
    )
  }
  if (section.howToUse) {
    return (
      <HowToUseBlock>
        <div className='how-to-heading'>店家怎麼用</div>
        <Text>{section.howToUse}</Text>
      </HowToUseBlock>
    )
  }
  return null
}

/** 單一平台的公告清單（分頁內容） */
const PlatformAnnouncements = ({ platform }) => {
  const visibleAnnouncements = useMemo(
    () => buildShopAnnouncements(ANNOUNCEMENTS, platform),
    [platform]
  )
  const latest = latestVersionOf(platform)

  if (!visibleAnnouncements.length) {
    return <Empty description='目前沒有公告' />
  }

  return (
    <>
      {visibleAnnouncements.map((ann) => (
        <VersionCard
          key={ann.version}
          title={
            <VersionHeader>
              <span className='version-title'>{ann.title}</span>
              <span className='version-date'>{ann.version}</span>
              {ann.version === latest && (
                <Tag color='magenta' style={{ marginLeft: 4 }}>
                  最新
                </Tag>
              )}
            </VersionHeader>
          }
        >
          {ann.summary && (
            <Paragraph style={{ color: '#4b5563', whiteSpace: 'pre-line' }}>
              {ann.summary}
            </Paragraph>
          )}

          {/* 預設全部收合：一張卡片只顯示標題與摘要，內容用開關（展開箭頭）打開 */}
          <Collapse ghost>
            {ann.sections.map((section, i) => {
              const meta = KIND_META[section.kind] || KIND_META.improvement
              return (
                <Panel
                  key={`${ann.version}-${i}`}
                  header={
                    <SectionTitleRow>
                      <Tag color={meta.color} icon={meta.icon}>
                        {meta.label}
                      </Tag>
                      <span>{section.title}</span>
                    </SectionTitleRow>
                  }
                >
                  {section.description && (
                    <Paragraph
                      style={{ marginBottom: 8, whiteSpace: 'pre-line' }}
                    >
                      {section.description}
                    </Paragraph>
                  )}
                  {renderHowToUse(section)}
                </Panel>
              )
            })}
          </Collapse>
        </VersionCard>
      ))}
    </>
  )
}

const AnnouncementsPage = () => {
  const [activeKey, setActiveKey] = useState(() => {
    // 哪一邊有未讀就先開哪一邊，店家不用自己找
    const unread = ANNOUNCEMENT_PLATFORMS.find((p) =>
      hasUnreadAnnouncement(p.key)
    )
    return unread?.key || ANNOUNCEMENT_PLATFORMS[0].key
  })

  /**
   * 把「正在看的那個分頁」標記成已讀。
   *
   * 刻意只標記當前分頁 —— 一進來就把兩邊都標掉的話，
   * 店家只看了網頁那頁，App 的新功能紅點也一起消失，就永遠不會知道了。
   */
  useEffect(() => {
    try {
      window.localStorage.setItem(
        readKeyOf(activeKey),
        latestVersionOf(activeKey)
      )
    } catch (e) {
      // localStorage 被禁用就算了，不影響顯示
    }
  }, [activeKey])

  const items = ANNOUNCEMENT_PLATFORMS.map((p) => ({
    key: p.key,
    label: (
      // 只有「還沒看過而且不是現在這一頁」才亮點，避免看著看著紅點還在
      <Badge dot={p.key !== activeKey && hasUnreadAnnouncement(p.key)}>
        <span style={{ paddingRight: 4 }}>{p.label}</span>
      </Badge>
    ),
    children: <PlatformAnnouncements platform={p.key} />
  }))

  return (
    <PageContainer>
      <PageHeader>
        <BellOutlined style={{ fontSize: 24, color: '#7c3aed' }} />
        <h1>更新公告</h1>
      </PageHeader>
      <SubHint>
        系統的最新更新與功能說明都會放在這裡。網頁後台與手機 App
        的更新分開兩個分頁，每筆公告下面會列出「店家怎麼用」的操作步驟。
      </SubHint>

      <Tabs activeKey={activeKey} onChange={setActiveKey} items={items} />
    </PageContainer>
  )
}

export default AnnouncementsPage
